import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Policy, PolicySeverity, PolicyCategory, PolicyStatus } from '../types/policies';
import { PolicyCard } from '../components/policies/PolicyCard';
import { PolicyStats } from '../components/policies/PolicyStats';
import { PolicyDetailsEdit } from '../components/policies/PolicyDetailsEdit';
import { CategoryFilter } from '../components/policies/CategoryFilter';
import { SeverityFilter } from '../components/policies/SeverityFilter';
import { StatusFilter } from '../components/policies/StatusFilter';
import { CreatePolicyChat } from '../components/policies/CreatePolicyChat';
import { Button } from '../../../ui/common-ui/button';
import { Skeleton } from '../../../ui/common-ui/skeleton';
import { Input } from '../../../ui/common-ui/input';
import { toast } from 'sonner';
import {
  Search,
  ShieldCheck,
  Plus,
  Play,
  X,
  Loader2,
} from 'lucide-react';
import {
  fetchPolicies,
  enablePolicy,
  disablePolicy,
  deletePolicy,
  executePolicies,
  getApiErrorMessage,
} from '../services/api';
import { DeleteConfirmDialog } from '../../DeleteConfirmDialog';
import { normalizeCloudPolicies, normalizePolicySeverity } from '../lib/policyTransforms';
import { CloudSelector } from '../../CloudDashboard/components/CloudSelector';
import type { CloudType, Tenant, CloudResource } from '../../CloudDashboard/types/cloud';
import { fetchTenants, fetchAwsAccounts, fetchGcpProjectIds, fetchAzureSubscriptions } from '../../CloudDashboard/services/api';
import {
  persistSelectedResource,
  resolveCloudSelection,
  resolveResourceForCloud,
} from '../../CloudDashboard/lib/cloudSelection';
import { myLocalStorage } from '../../../StorageHelper';


const RUNNING_POLL_INTERVAL_MS = 4000;
const IDLE_POLL_INTERVAL_MS = 30000;

type ViewMode = 'manage' | 'create';

const cloudLabels: Record<CloudType, string> = {
  AWS: 'AWS',
  GCP: 'GCP',
  AZURE: 'Azure',
};

const isEffectivelyActive = (status: PolicyStatus) => status === 'ACTIVE' || status === 'ENABLING';
const isEffectivelyInactive = (status: PolicyStatus) => status === 'INACTIVE' || status === 'DISABLING';
const isPolicyStatus = (status: unknown): status is PolicyStatus => (
  status === 'ACTIVE' || status === 'INACTIVE' || status === 'ENABLING' || status === 'DISABLING'
);

const policyKey = (policy: Policy) => String(policy.policyId || policy.id);

interface PolicyAutopilotPageProps {
  lockedCloud?: CloudType;
}

export default function PolicyAutopilotPage({ lockedCloud }: PolicyAutopilotPageProps) {
  const location = useLocation();
  const state = location.state as { preLinkedAlertId?: string; selectedCloud?: CloudType } | null;
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState<PolicySeverity | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PolicyCategory | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<PolicyStatus | null>(null);
  const [selectedCloud, setSelectedCloud] = useState<CloudType>(lockedCloud || state?.selectedCloud || 'AWS');
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<Policy | null>(null);
  const [isDeletingPolicy, setIsDeletingPolicy] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(state?.preLinkedAlertId ? 'create' : 'manage');
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [cloudResources, setCloudResources] = useState<Record<CloudType, CloudResource[]>>({ AWS: [], GCP: [], AZURE: [] });
  const [isLoadingSelector, setIsLoadingSelector] = useState(true);
  const [selectedPolicyKeys, setSelectedPolicyKeys] = useState<Set<string>>(new Set());
  const [isExecutingBulk, setIsExecutingBulk] = useState(false);
  const refreshInFlightRef = useRef(false);
  const policiesRef = useRef<Policy[]>([]);
  policiesRef.current = policies;


  useEffect(() => {
    const loadTenants = async () => {
      try {
        const response = await fetchTenants();
        const tenants: Tenant[] = response.map((t: any) => ({ ...t }));
        setTenantList(tenants);

        if (tenants.length > 0) {
          const storedTenant = myLocalStorage.getItem('latestTenant');
          const storedTenantName =
            typeof storedTenant === 'string' ? storedTenant : storedTenant?.tenantName;
          const selected =
            tenants.find(t => t.tenantName === storedTenantName) || tenants[0];

          setSelectedTenant(selected);
          myLocalStorage.setItem('latestTenant', selected);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      } finally {
        setIsLoadingSelector(false);
      }
    };

    loadTenants();
  }, []);

  useEffect(() => {
    if (!selectedTenant || !selectedResource) {
      setPolicies([]);
      setIsLoading(false);
      return;
    }

    let isCurrentRequest = true;

    const loadPolicies = async () => {
      setIsLoading(true);
      try {
        const data = await fetchPolicies(
          selectedTenant.tenantName,
          selectedResource.id,
          selectedCloud
        );
        if (isCurrentRequest) {
          setPolicies(normalizeCloudPolicies(data));
        }
      } catch (error) {
        console.error('Failed to fetch policies:', error);
        if (isCurrentRequest) {
          setPolicies([]);
          toast.error('Failed to load policies');
        }
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    loadPolicies();

    return () => {
      isCurrentRequest = false;
    };
  }, [selectedTenant, selectedResource, selectedCloud]);

  useEffect(() => {
    if (!selectedTenant) return;

    const loadCloudResources = async () => {
      const tenantName = selectedTenant.tenantName;

      try {
        const [gcpProjects, awsAccounts, azureSubs] = await Promise.all([
          fetchGcpProjectIds(tenantName).catch(() => []),
          fetchAwsAccounts(tenantName).catch(() => []),
          fetchAzureSubscriptions(tenantName).catch(() => [])
        ]);

        const gcpResources: CloudResource[] = gcpProjects.map((projectId: string) => ({
          id: projectId,
          name: projectId
        }));

        const awsResources: CloudResource[] = awsAccounts.map((account: any) => ({
          id: account.accountId,
          name: account.accountName
        }));

        const azureResources: CloudResource[] = azureSubs.map((sub: any) => ({
          id: sub.azureSubscriptionId,
          name: sub.subscriptionName
        }));

        const newCloudResources = {
          GCP: gcpResources,
          AWS: awsResources,
          AZURE: azureResources
        };

        setCloudResources(newCloudResources);

        if (lockedCloud) return;

        const { cloud, resource } = resolveCloudSelection(newCloudResources, selectedCloud);

        if (cloud) {
          setSelectedCloud(cloud);
          setSelectedResource(resource);
        }
      } catch (error) {
        console.error('Failed to fetch cloud resources:', error);
      }
    };

    loadCloudResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  useEffect(() => {
    if (!lockedCloud) return;

    setSelectedCloud(lockedCloud);
    setPolicies([]);
    setSelectedResource(resolveResourceForCloud(lockedCloud, cloudResources[lockedCloud] || []));
  }, [lockedCloud, cloudResources]);

  const handleCloudChange = (cloud: CloudType) => {
    if (lockedCloud) return;

    const resources = cloudResources[cloud];
    if (!resources || resources.length === 0) return;

    setSelectedCloud(cloud);
    setPolicies([]);
    setSelectedResource(resolveResourceForCloud(cloud, resources));
  };

  const handleResourceChange = (resource: CloudResource) => {
    setSelectedResource(resource);
    persistSelectedResource(selectedCloud, resource);
  };

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    myLocalStorage.setItem('latestTenant', tenant);
    setCloudResources({ AWS: [], GCP: [], AZURE: [] });
    setSelectedResource(null);
    toast.success(`Switched to ${tenant.tenantName}`);
  };

  const refreshPolicies = useCallback(async () => {
    if (!selectedTenant || !selectedResource || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const data = await fetchPolicies(selectedTenant.tenantName, selectedResource.id, selectedCloud);
      const refreshed = normalizeCloudPolicies(data);
      const previous = new Map(policiesRef.current.map(policy => [policyKey(policy), policy]));
      refreshed.forEach(policy => {
        const before = previous.get(policyKey(policy));
        const wasRunning = before?.policyStatus === 'ENABLING' || before?.policyStatus === 'DISABLING';
        const isRunning = policy.policyStatus === 'ENABLING' || policy.policyStatus === 'DISABLING';
        if (!wasRunning || isRunning) return;
        if (policy.lastExecutionStatus === 'FAILED') {
          toast.error(`${policy.title} failed`, { description: policy.lastExecutionReason || undefined });
        } else if (policy.lastExecutionStatus === 'SUCCESS') {
          toast.success(`${policy.title} finished`);
        }
      });
      setPolicies(refreshed);
    } catch (error) {
      console.error('Failed to refresh policies:', error);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [selectedTenant, selectedResource, selectedCloud]);

  const hasRunningPolicies = policies.some(policy => policy.policyStatus === 'ENABLING' || policy.policyStatus === 'DISABLING');

  useEffect(() => {
    if (viewMode !== 'manage' || !selectedTenant || !selectedResource) return;
    const intervalId = window.setInterval(
      () => void refreshPolicies(),
      hasRunningPolicies ? RUNNING_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
    );
    return () => window.clearInterval(intervalId);
  }, [viewMode, selectedTenant, selectedResource, hasRunningPolicies, refreshPolicies]);

  useEffect(() => {
    setSelectedPolicyKeys(new Set());
  }, [selectedTenant, selectedResource, selectedCloud]);

  const togglePolicySelection = (policy: Policy, selected: boolean) => {
    setSelectedPolicyKeys(prev => {
      const next = new Set(prev);
      if (selected) next.add(policyKey(policy));
      else next.delete(policyKey(policy));
      return next;
    });
  };

  const handleExecuteSelected = async () => {
    if (!selectedTenant || !selectedResource || selectedPolicyKeys.size === 0) return;
    const targets = policies.filter(policy => selectedPolicyKeys.has(policyKey(policy)));
    setIsExecutingBulk(true);
    try {
      const responses = await executePolicies(
        targets.map(policyKey),
        selectedCloud,
        selectedTenant.tenantName,
        selectedResource.id,
      );
      const byKey = new Map(responses.map(response => [String(response.policyId), response]));
      setPolicies(prev => prev.map(policy => {
        const response = byKey.get(policyKey(policy));
        return response?.success && isPolicyStatus(response.status) ? { ...policy, policyStatus: response.status } : policy;
      }));
      const failed = responses.filter(response => !response.success);
      const started = responses.length - failed.length;
      if (started > 0) toast.success(`Started ${started} polic${started === 1 ? 'y' : 'ies'} in parallel`);
      if (failed.length > 0) toast.error(failed[0].message || `${failed.length} polic${failed.length === 1 ? 'y' : 'ies'} could not start`);
      setSelectedPolicyKeys(new Set());
    } catch (error) {
      console.error('Failed to execute policies:', error);
      toast.error(getApiErrorMessage(error, 'Failed to run the selected policies'));
    } finally {
      setIsExecutingBulk(false);
    }
  };

  const handleTogglePolicy = async (id: string | number, enabled: boolean) => {
    const policy = policies.find(p => p.id === id);
    if (!policy || !selectedTenant || !selectedResource) return;
    const requestedStatus: PolicyStatus = enabled ? 'ENABLING' : 'DISABLING';
    const fallbackStatus: PolicyStatus = enabled ? 'ACTIVE' : 'INACTIVE';
    const requestCloudType = policy.cloudType === selectedCloud ? policy.cloudType : selectedCloud;
    const requestCloudId = policy.cloudAccountId || selectedResource.id;

    setPolicies(prev =>
      prev.map(p =>
        p.id === id
          ? { ...p, cloudType: requestCloudType, cloudAccountId: requestCloudId, policyStatus: requestedStatus }
          : p
      )
    );

    try {
      let response;
      if (enabled) {
        response = await enablePolicy(
          policy.policyId || String(id),
          requestCloudType,
          selectedTenant.tenantName,
          requestCloudId
        );
      } else {
        response = await disablePolicy(
          policy.policyId || String(id),
          requestCloudType,
          selectedTenant.tenantName,
          requestCloudId
        );
      }

      if (response && response.success) {
        const nextStatus = isPolicyStatus(response.status) ? response.status : fallbackStatus;
        setPolicies(prev =>
          prev.map(p =>
            p.id === id
              ? { ...p, cloudType: requestCloudType, cloudAccountId: requestCloudId, policyStatus: nextStatus }
              : p
          )
        );
        toast.success(
          nextStatus === 'ENABLING'
            ? 'Policy run started'
            : nextStatus === 'DISABLING'
            ? 'Policy rollback started'
            : enabled ? 'Policy armed' : 'Policy deactivated'
        );
      } else {
        throw new Error('Action failed');
      }
    } catch (error) {
      console.error('Failed to toggle policy:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update policy status'));
      setPolicies(prev =>
        prev.map(p =>
          p.id === id
            ? { ...p, policyStatus: enabled ? 'INACTIVE' : 'ACTIVE' }
            : p
        )
      );
    }
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete || !selectedTenant || !selectedResource) return;
    setIsDeletingPolicy(true);
    try {
      await deletePolicy(
        policyToDelete.policyId || String(policyToDelete.id),
        selectedCloud,
        selectedTenant.tenantName,
        policyToDelete.cloudAccountId || selectedResource.id
      );
      setPolicies(prev => prev.filter(p => p.id !== policyToDelete.id));
      toast.success('Policy deleted');
      setPolicyToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete policy:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete policy'));
    } finally {
      setIsDeletingPolicy(false);
    }
  };

  const handleViewDetails = (policy: Policy) => {
    setSelectedPolicy(policy);
    setDetailsOpen(true);
  };

  const statusCounts = useMemo(() => ({
    active: policies.filter(p => isEffectivelyActive(p.policyStatus)).length,
    inactive: policies.filter(p => isEffectivelyInactive(p.policyStatus)).length,
  }), [policies]);

  const filteredPolicies = useMemo(() => {
    const severityOrder: Record<PolicySeverity, number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3,
    };

    let filtered = [...policies];

    if (selectedCloud) {
      filtered = filtered.filter(p => p.cloudType === selectedCloud);
    }

    if (selectedStatus !== null) {
      filtered = filtered.filter(p => {
        if (selectedStatus === 'ACTIVE' && !isEffectivelyActive(p.policyStatus)) return false;
        if (selectedStatus === 'INACTIVE' && !isEffectivelyInactive(p.policyStatus)) return false;
        return true;
      });
    }

    if (selectedCategory !== null) {
      filtered = filtered.filter(p => p.cloudPolicyType === selectedCategory);
    }

    if (selectedSeverity !== null) {
      filtered = filtered.filter(p => normalizePolicySeverity(p.severity) === selectedSeverity);
    }

    if (searchQuery.trim()) {
      const queryTokens = searchQuery
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      filtered = filtered.filter((p) => {
        const searchText = `${p.title} ${p.description}`.toLowerCase();
        return queryTokens.every(token => searchText.includes(token));
      });
    }

    return [...filtered].sort((a, b) => {
      const aSeverity = normalizePolicySeverity(a.severity);
      const bSeverity = normalizePolicySeverity(b.severity);
      return (severityOrder[aSeverity] ?? 99) - (severityOrder[bSeverity] ?? 99);
    });
  }, [policies, selectedStatus, selectedCategory, selectedSeverity, selectedCloud, searchQuery]);

  const activeFilterCount = [selectedStatus, selectedCategory, selectedSeverity].filter(Boolean).length;

  const categoryCounts = useMemo(() => ({
    guardrail: policies.filter(p => p.cloudPolicyType === 'GUARDRAIL').length,
    remediation: policies.filter(p => p.cloudPolicyType === 'REMEDIATION').length,
  }), [policies]);



  return (
    <div className="min-h-screen bg-background">
      <main className="px-6 py-6">
        {/* Back button for create mode */}
        <div className="mb-6">
          <CloudSelector
            tenantList={tenantList}
            selectedTenant={selectedTenant}
            selectedCloud={selectedCloud}
            selectedResource={selectedResource}
            cloudResources={cloudResources}
            onTenantChange={handleTenantChange}
            onCloudChange={handleCloudChange}
            onResourceChange={handleResourceChange}
            hideCloudSwitcher={Boolean(lockedCloud)}
            isLoading={isLoadingSelector}
            onBack={viewMode === 'create' ? () => setViewMode('manage') : undefined}
            actionButton={
              viewMode === 'manage' ? (
                <button
                  onClick={() => setViewMode('create')}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-[13px] tracking-wide uppercase text-white bg-violet-600 border border-violet-600 shadow-md shadow-violet-500/20 transition-all duration-300 hover:bg-violet-700 hover:border-violet-700 hover:scale-[1.02] hover:shadow-lg hover:shadow-violet-500/30 active:scale-[0.98]"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden md:inline">Create Policy</span>
                </button>
              ) : undefined
            }
          />
        </div>

        {/* Stats - only in manage mode */}
        {viewMode === 'manage' && (
          <div className="mb-6">
            <PolicyStats policies={policies} />
          </div>
        )}

        {viewMode === 'manage' && (
          <div className="space-y-3 mb-6">
            <div className="w-full flex items-center p-2 rounded-2xl bg-card backdrop-blur-xl border portal-card-elevated">

              {/* Left Side Group */}
              <div className="flex-1 flex items-center justify-start gap-4 lg:gap-8">
                <div className="flex flex-col items-start min-w-0 w-full max-w-[200px]">
                  <StatusFilter
                    selectedStatus={selectedStatus}
                    onStatusChange={setSelectedStatus}
                    counts={statusCounts}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />

              {/* Middle Group */}
              <div className="flex-none flex items-center justify-center ml-4 mr-4">
                <CategoryFilter
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  counts={categoryCounts}
                />
              </div>

              {/* Divider */}
              <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />

              {/* Right Side Group */}
              <div className="flex-[1.5] flex items-center justify-end gap-4 lg:gap-8">
                <div className="flex flex-col items-end min-w-0 w-full max-w-[200px]">
                  <SeverityFilter
                    selectedSeverity={selectedSeverity}
                    onSeverityChange={setSelectedSeverity}
                  />
                </div>
              </div>

            </div>

            {/* Search bar - above cards */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 rounded-2xl bg-card backdrop-blur-xl border portal-card-elevated transition-colors duration-300 focus-within:!border-sky-500/30">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search policies by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 h-12 w-full bg-transparent border-0 shadow-none focus-visible:ring-0 text-[14px]"
                />
              </div>
            </div>
          </div>
        )}

        {/* ─── MANAGE VIEW ─── */}
        {viewMode === 'manage' && (
          <>
            {/* Results count */}
            {/* Removed top-right clear filters button per user request */}

            {/* Loading skeletons */}
            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 60}ms` }}>
                    <div className="bg-card border border-border rounded-2xl p-5 space-y-4 portal-card-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-5 w-24 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <Skeleton className="h-5 w-10 rounded-full" />
                      </div>
                      <Skeleton className="h-5 w-4/5" />
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4 w-14" />
                        <Skeleton className="h-4 w-10" />
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                        <Skeleton className="h-3 w-16" />
                        <div className="flex gap-2">
                          <Skeleton className="h-5 w-24 rounded-md" />
                          <Skeleton className="h-5 w-20 rounded-md" />
                        </div>
                      </div>
                      <div className="flex justify-between pt-1">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                      <Skeleton className="h-9 w-full rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredPolicies.length === 0 && (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/50 mb-4">
                  <ShieldCheck className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-lg font-medium text-foreground mb-1">No policies found</p>
                <p className="text-sm text-muted-foreground mb-6">
                  {searchQuery || activeFilterCount > 0
                    ? 'Try adjusting your search or filters'
                    : `No policies configured for ${cloudLabels[selectedCloud]} yet`}
                </p>
                {(searchQuery || activeFilterCount > 0) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedStatus(null);
                      setSelectedCategory(null);
                      setSelectedSeverity(null);
                      setSearchQuery('');
                    }}
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            )}

            {/* Policy cards grid */}
            {!isLoading && filteredPolicies.length > 0 && (
              <div
                key={`${selectedCategory}-${selectedSeverity}-${selectedStatus}-${selectedCloud}-${searchQuery}`}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredPolicies.map((policy, index) => (
                  <div
                    key={`${policy.id}-${selectedCategory}-${selectedSeverity}-${selectedStatus}-${selectedCloud}-${searchQuery}`}
                    className="animate-scale-in"
                    style={{
                      animationDelay: `${index * 40}ms`,
                      animationFillMode: 'forwards',
                      animationDuration: '0.3s',
                    }}
                  >
                    <PolicyCard
                      policy={policy}
                      onViewDetails={handleViewDetails}
                      onToggle={handleTogglePolicy}
                      onDelete={setPolicyToDelete}
                      selectable
                      selected={selectedPolicyKeys.has(policyKey(policy))}
                      onSelectChange={togglePolicySelection}
                    />
                  </div>
                ))}
              </div>
            )}
            {selectedPolicyKeys.size > 0 && (
              <div className="sticky bottom-6 z-40 mt-6 flex justify-center pointer-events-none">
                <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-2.5 shadow-[0_12px_32px_rgb(15_23_42/0.16)]">
                  <span className="text-sm font-medium text-foreground">
                    {selectedPolicyKeys.size} selected
                  </span>
                  <span className="text-xs text-muted-foreground hidden sm:inline">Each policy runs in its own session</span>
                  <Button
                    onClick={handleExecuteSelected}
                    disabled={isExecutingBulk}
                    className="h-9 rounded-xl px-4"
                  >
                    {isExecutingBulk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                    Run {selectedPolicyKeys.size}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-xl"
                    onClick={() => setSelectedPolicyKeys(new Set())}
                    aria-label="Clear selection"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ─── CREATE VIEW ─── */}
        {viewMode === 'create' && (
          <CreatePolicyChat
            selectedCloud={selectedCloud}
            tenantName={selectedTenant?.tenantName}
            cloudId={selectedResource?.id}
            initialType={state?.preLinkedAlertId ? 'remediation' : undefined}
            initialLinkedAlerts={state?.preLinkedAlertId ? [state.preLinkedAlertId] : undefined}
            onPoliciesAdded={(addedPolicies) => {
              setPolicies(prev => {
                const combined = [...normalizeCloudPolicies(addedPolicies), ...prev];
                const seen = new Set<string | number | undefined>();
                return combined.filter(policy => {
                  const key = policy.policyId || policy.id;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
              });
              setViewMode('manage');
            }}
          />
        )}
      </main>

      {/* Details / Edit Dialog */}
      <PolicyDetailsEdit
        policy={selectedPolicy}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        tenantName={selectedTenant?.tenantName}
        cloudId={selectedResource?.id}
        onPolicyUpdate={(updatedPolicy) => {
          setPolicies(prev => prev.map(policy => (
            (policy.policyId || policy.id) === (updatedPolicy.policyId || updatedPolicy.id)
              ? updatedPolicy
              : policy
          )));
          setSelectedPolicy(updatedPolicy);
        }}
        onRefreshRequested={refreshPolicies}
      />

      <DeleteConfirmDialog
        open={policyToDelete !== null}
        title="Delete policy"
        description={`"${policyToDelete?.title || ''}" will be permanently deleted. This action cannot be undone.`}
        isDeleting={isDeletingPolicy}
        onConfirm={handleDeletePolicy}
        onCancel={() => setPolicyToDelete(null)}
      />
    </div>
  );
}
